<template>
    <!-- Los partidos se cargan en segundo plano: el select se renderiza desde
         el primer render (options vacías) y el propio BaseMultiselect muestra
         su estado de carga, igual que LfpEventPicker. Así no hay salto de
         layout ni un skeleton intermedio que descuadre la tarjeta. -->
    <div v-if="error" class="tw:space-y-2">
        <BaseMultiselect
            label="Partido Asobal-Avanza"
            name="evo_match_id"
            :value="null"
            :options="[]"
            :disabled="true"
        />
        <div class="tw:flex tw:items-center tw:gap-2">
            <span class="tw:text-xs tw:text-gray-500">{{ 'No se pudieron cargar los partidos de AvanzaEsp.' }}</span>
            <Button
                type="button"
                variant="ghost"
                :label="'Recargar partidos'"
                @click="reload"
            />
        </div>
    </div>
    <BaseMultiselect
        v-else
        label="Partido Asobal-Avanza"
        name="evo_match_id"
        :value="selected?.id ?? null"
        :options="matches || []"
        value-key="id"
        :loading="loading"
        :disabled="loading"
        rules="evoPartidoRequerido"
        @input="onSelect"
    >
        <!-- El label de un partido es compuesto ("Local vs Visitante"), así que
             se pinta por slot: `label-key` solo cubriría una clave plana. -->
        <template #singleLabel="{ option }">
            {{ optionLabel(option) }}
        </template>
        <template #option="{ option }">
            {{ optionLabel(option) }}
        </template>
    </BaseMultiselect>
</template>

<script setup>
    import { ref, watch, onMounted } from 'vue';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';
    import Button from '@/modules/ui/Button.vue';
    import { useEvoEvents } from '@/composables/admin/events/useEvoEvents';

    const props = defineProps({
        evoMatchId: {
            // El backend puede entregar el ID como string o número según el
            // endpoint; se normaliza la comparación en applyPreselection.
            type: [String, Number],
            default: null
        }
    });

    const emit = defineEmits(['selected', 'cleared']);

    const { matches, loading, error, loadMatches } = useEvoEvents();

    const selected = ref(null);

    // Preselección: al cargar los partidos, si el evento ya tiene un partido
    // vinculado, se marca en el picker. Si evoMatchId es null, se limpia.
    // La comparación normaliza ambos lados a string: el endpoint puede devolver
    // IDs numéricos y el evento persistido entregarlos como texto (o al revés).
    function applyPreselection() {
        if (props.evoMatchId && matches.value.length) {
            selected.value = matches.value.find(el => String(el.id) === String(props.evoMatchId)) || null;
        } else {
            selected.value = null;
        }
    }

    // Recarga encapsulada: tras cargar los partidos se reaplica la preselección
    // (cubre el caso de primera carga fallida + "Recargar partidos" al editar).
    async function reload() {
        await loadMatches();
        applyPreselection();
    }

    // BaseMultiselect emite el valor seleccionado desde su `@input` (el
    // componente interno de vue-multiselect ya normaliza null al deseleccionar
    // en modo single), así que un único handler cubre selección y limpieza.
    // BaseMultiselect.handleSelect emite `input` con el VALOR PLANO
    // (`getOptionValue(option)`), es decir el id — no el objeto. Aquí se
    // reconstruye el objeto del partido a partir de ese id para que:
    //   - el `:value` del select (que lee `selected.id`) siga siendo el id y
    //     vee-validate pueda revalidar al seleccionar, limpiando su error;
    //   - `selected` conserve el partido completo, que es lo que el label
    //     compuesto necesita para pintar "Local vs Visitante".
    // Un único handler cubre selección y limpieza (null al deseleccionar).
    function onSelect(value) {
        if (value === null || value === undefined || value === '') {
            if (selected.value === null) {
                return;
            }

            selected.value = null;
            emit('cleared');
            return;
        }

        // El id puede llegar como string o número según el endpoint; la
        // comparación se normaliza, igual que en applyPreselection.
        const match = (matches.value || []).find(m => String(m.id) === String(value)) || null;

        selected.value = match || { id: value };
        emit('selected', selected.value);
    }

    // Los partidos de AvanzaEsp llegan con home_club_name y away_club_name
    // (p.ej. "Real Murcia vs FC Cartagena"). Se mantienen fallbacks por
    // robustez: `name`, luego home_club/away_club y, en último caso, el id.
    function optionLabel(option) {
        if (!option) {
            return '';
        }

        if (option.home_club_name && option.away_club_name) {
            return `${option.home_club_name} vs ${option.away_club_name}`;
        }

        if (option.name) {
            return option.name;
        }

        const home = clubName(option.home_club);
        const away = clubName(option.away_club);

        if (home && away) {
            return `${home} vs ${away}`;
        }

        return option.id;
    }

    function clubName(club) {
        if (!club) {
            return null;
        }

        return typeof club === 'string' ? club : (club.name || club.nombre || null);
    }

    watch(() => props.evoMatchId, () => {
        applyPreselection();
    });

    onMounted(async() => {
        await reload();
    });
</script>
